import React, { useEffect, useState } from 'react';

import Button from '@components/Button';
import Input from '@components/Input';
import ModalBase from '@components/ModalBase';

import './style.scss';
interface PhoneEditModalProps {
  show: boolean;
  currentPhone: string;
  onClose: () => void;
  onInitiate: (phone: string) => Promise<void> | void;
  onVerify: (code: string) => Promise<void> | void;
  isInitiating?: boolean;
  isVerifying?: boolean;
  initiateError?: string;
  verifyError?: string;
  transactionId?: string;
  expiresAt?: string;
}

export default function PhoneEditModal({
  show,
  currentPhone,
  onClose,
  onInitiate,
  onVerify,
  isInitiating = false,
  isVerifying = false,
  initiateError = '',
  verifyError = '',
  transactionId = '',
  expiresAt = '',
}: PhoneEditModalProps) {
  const [newPhone, setNewPhone] = useState('');
  const [code, setCode] = useState('');
  const [localError, setLocalError] = useState('');

  useEffect(() => {
    if (show) {
      setNewPhone('');
      setCode('');
      setLocalError('');
    }
  }, [show]);

  const handleInitiate = async () => {
    if (!newPhone.trim()) {
      setLocalError('Phone number is required');
      return;
    }
    setLocalError('');
    await onInitiate(newPhone.trim());
  };

  const handleVerify = async () => {
    if (!code.trim()) {
      setLocalError('Verification code is required');
      return;
    }
    setLocalError('');
    await onVerify(code.trim());
  };

  const body = (
    <div className="phone-edit-modal__body">
      <h3 className="phone-edit-modal__title">Enter your new phone number</h3>
      <div className="phone-edit-modal__current">Current: {currentPhone || 'Not provided'}</div>
      <div className="phone-edit-modal__form">
        <Input
          field={{ label: 'New Phone (E.164)', type: CustomFieldType.TEXT, value: newPhone, isRequired: true }}
          name="newPhone"
          className="phone-edit-modal__input"
          onChange={(e) => setNewPhone((e.target as HTMLInputElement).value)}
          placeholder="+1 555 123 4567"
        />
        <div className="phone-edit-modal__notice">
          <p>You will receive an SMS with a verification code.</p>
        </div>
        {(localError || initiateError) && <div className="error-message">{localError || initiateError}</div>}
        <div className="phone-edit-modal__actions">
          <Button
            bsStyle={ButtonStyle.DEFAULT}
            onClick={onClose}
            content="Cancel"
            disabled={isInitiating || isVerifying}
          />
          <Button
            bsStyle={ButtonStyle.PRIMARY}
            onClick={handleInitiate}
            content="Send Code"
            disabled={isInitiating}
            showLoader={isInitiating}
          />
        </div>
      </div>

      {transactionId && (
        <div className="phone-edit-modal__verify">
          <Input
            field={{ label: 'Verification Code', type: CustomFieldType.TEXT, value: code, isRequired: true }}
            name="otpCode"
            className="phone-edit-modal__input"
            onChange={(e) => setCode((e.target as HTMLInputElement).value)}
            placeholder="Enter code"
          />
          {expiresAt && <div className="helper-text">Code expires at: {new Date(expiresAt).toLocaleTimeString()}</div>}
          {verifyError && <div className="error-message">{verifyError}</div>}
          <div className="phone-edit-modal__actions">
            <Button bsStyle={ButtonStyle.DEFAULT} onClick={onClose} content="Close" disabled={isVerifying} />
            <Button
              bsStyle={ButtonStyle.PRIMARY}
              onClick={handleVerify}
              content="Verify"
              disabled={isVerifying}
              showLoader={isVerifying}
            />
          </div>
        </div>
      )}
    </div>
  );

  return (
    <ModalBase
      show={show}
      onClose={onClose}
      closeButton={true}
      titleElement="Edit Phone"
      bodyElement={body}
      className="phone-edit-modal"
      closeOnEscape={!isInitiating && !isVerifying}
      closeOnOverlayClick={!isInitiating && !isVerifying}
      showFooter={true}
      footerElement={' '}
    />
  );
}
